import { notFound } from 'next/navigation';
import { getRequestConfig } from 'next-intl/server';

// Define supported locales
export const locales = ['en', 'en-AU', 'en-NZ', 'nl', 'si'] as const;
export const defaultLocale = 'en' as const;

export type Locale = (typeof locales)[number];

// Type guard to check if a string is a valid locale
export function isValidLocale(locale: string): locale is Locale {
  return (locales as ReadonlyArray<string>).includes(locale);
}

// Define the locale prefix handling
export const localePrefix = 'as-needed' as const; // Options: 'always' | 'as-needed' | 'never'

// Define pathnames for static export
export const pathnames = {
  // Define any pathnames that should be localized
  // Example:
  // '/about': {
  //   en: '/about',
  //   'en-AU': '/about-au',
  // },
} as const;

// Validate that the incoming `locale` parameter is valid
export default getRequestConfig(async ({ locale }) => {
  // Ensure locale is a valid value or use default
  const validLocale = locale && isValidLocale(locale) ? locale : defaultLocale;
  
  // If no valid locale is found, return 404
  if (!isValidLocale(validLocale)) {
    notFound();
  }

  try {
    // Dynamically import the messages for the requested locale
    const messages = (await import(`../messages/${validLocale}.json`)).default;
    
    return {
      locale: validLocale,
      messages,
      // Time zone and formatting options are now configured in next-intl.config.js
    };
  } catch (error) {
    console.error(`Failed to load messages for locale: ${validLocale}`, error);
    notFound();
  }
});
